/**
 * (Spostato il 29/10/2026 da `console-app/src/components/mappa/` in `shared/mappa/`, identico: lo usano la mappa delle
 * aziende della console e la cartina delle aree di interesse del gestionale. Puro, niente oltre ES2017.)
 *
 * La camera della mappa: quale parte del mondo si vede e a che ingrandimento. Pura.
 *
 * `cx`/`cy` sono il centro della vista nelle coordinate del mondo (`geometria.ts`), `scala` i pixel per unità del
 * mondo. I margini sono le zone coperte da altro (titolo e filtri in alto, il pannello in basso sul telefono):
 * l'inquadratura mette l'Italia nello spazio che resta libero, non sotto i comandi.
 */
import type { Punto, Riquadro } from './geometria';

export interface Camera {
  cx: number;
  cy: number;
  scala: number;
}

export interface Dimensioni {
  larghezza: number;
  altezza: number;
}

export interface Margini {
  alto: number;
  destra: number;
  basso: number;
  sinistra: number;
}

/** Fin dove si può avvicinare, rispetto alla vista di tutta l'Italia. */
export const INGRANDIMENTO_MASSIMO = 48;
const INGRANDIMENTO_MINIMO = 0.7;

export function inquadra(riquadro: Riquadro, dim: Dimensioni, margini: Margini, scalaMassima = Infinity): Camera {
  const libereL = Math.max(40, dim.larghezza - margini.sinistra - margini.destra);
  const libereA = Math.max(40, dim.altezza - margini.alto - margini.basso);
  const larghezza = Math.max(1e-6, riquadro.maxX - riquadro.minX);
  const altezza = Math.max(1e-6, riquadro.maxY - riquadro.minY);
  const scala = Math.min(libereL / larghezza, libereA / altezza, scalaMassima);
  // Il centro del riquadro va al centro dello spazio libero, non dello schermo.
  const spostamentoX = (margini.sinistra - margini.destra) / 2 / scala;
  const spostamentoY = (margini.alto - margini.basso) / 2 / scala;
  return {
    cx: (riquadro.minX + riquadro.maxX) / 2 - spostamentoX,
    cy: (riquadro.minY + riquadro.maxY) / 2 - spostamentoY,
    scala,
  };
}

export function versoSchermo(p: Punto, c: Camera, d: Dimensioni): Punto {
  return { x: (p.x - c.cx) * c.scala + d.larghezza / 2, y: (p.y - c.cy) * c.scala + d.altezza / 2 };
}

export function versoMondo(s: Punto, c: Camera, d: Dimensioni): Punto {
  return { x: (s.x - d.larghezza / 2) / c.scala + c.cx, y: (s.y - d.altezza / 2) / c.scala + c.cy };
}

export function viewBox(c: Camera, d: Dimensioni): string {
  const l = d.larghezza / c.scala;
  const a = d.altezza / c.scala;
  return `${c.cx - l / 2} ${c.cy - a / 2} ${l} ${a}`;
}

/** Tiene la camera fra i due ingrandimenti e col centro sopra l'Italia (non si «perde» la mappa trascinando). */
export function limita(c: Camera, scalaDiBase: number, riquadro: Riquadro): Camera {
  const scala = Math.min(scalaDiBase * INGRANDIMENTO_MASSIMO, Math.max(scalaDiBase * INGRANDIMENTO_MINIMO, c.scala));
  return {
    scala,
    cx: Math.min(riquadro.maxX, Math.max(riquadro.minX, c.cx)),
    cy: Math.min(riquadro.maxY, Math.max(riquadro.minY, c.cy)),
  };
}

/** Ingrandisce di `fattore` tenendo fermo sotto il dito (o il puntatore) il punto `schermo`. */
export function ingrandisciAttorno(c: Camera, d: Dimensioni, fattore: number, schermo: Punto): Camera {
  const fermo = versoMondo(schermo, c, d);
  const scala = c.scala * fattore;
  return {
    scala,
    cx: fermo.x - (schermo.x - d.larghezza / 2) / scala,
    cy: fermo.y - (schermo.y - d.altezza / 2) / scala,
  };
}

export function sposta(c: Camera, dxPixel: number, dyPixel: number): Camera {
  return { ...c, cx: c.cx - dxPixel / c.scala, cy: c.cy - dyPixel / c.scala };
}

/**
 * Il passaggio fra due camere, per `t` da 0 a 1. L'ingrandimento cambia in modo geometrico (su una scala logaritmica):
 * lineare, un salto da 1× a 20× sembrerebbe fermo all'inizio e precipitoso alla fine.
 */
export function interpola(a: Camera, b: Camera, t: number): Camera {
  const scala = a.scala * Math.pow(b.scala / a.scala, t);
  // Il centro si muove in proporzione all'ingrandimento, così il punto d'arrivo resta fermo sotto l'occhio.
  const peso = b.scala === a.scala ? t : (1 / a.scala - 1 / scala) / (1 / a.scala - 1 / b.scala);
  return { scala, cx: a.cx + (b.cx - a.cx) * peso, cy: a.cy + (b.cy - a.cy) * peso };
}

export const morbido = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
